/**
 * v136.0 — Identity bridge.
 *
 * The Forge runs as an iframe on a Shopify CDN origin (third-party to the
 * storefront). To know whether the visitor is signed in to a Shopify customer
 * account, we postMessage the parent storefront and read its first-party
 * identity payload.
 *
 * The Shopify section file (sections/arrow-builder-embed.liquid) listens for
 * our request, reads its own `window.__SPARROW_IDENTITY__` (emitted by Liquid
 * from `customer.*`), and posts the result back.
 *
 * If we don't get a reply within 500ms (e.g., bridge not deployed yet, or
 * older theme section file), we treat the user as anonymous. This is a
 * deliberate graceful-degradation — the Forge is fully functional without
 * sign-in.
 *
 * Why the host bridge (not a direct cookie / postMessage approach):
 *   - Cross-origin localStorage / cookies are blackholed by Safari ITP
 *   - Shopify's customer cookie is HttpOnly and not iframe-readable anyway
 *   - The Liquid template is the only place that can authoritatively emit
 *     the customer object — proxying it through postMessage is the cleanest
 *     boundary
 *
 * Security:
 *   - The Shopify section file computes an HMAC over the identity payload
 *     using a secret only the storefront knows. We forward that HMAC verbatim
 *     to the Worker. The Worker recomputes and rejects mismatches.
 *   - The iframe never sees the secret — only the resulting hash string.
 *   - Replay protection is layered on at the Worker: timestamps are bound
 *     into the upserted row's `updated_at` and conflict resolution rejects
 *     stale writes.
 */

export interface SparrowIdentity {
  signedIn: boolean;
  customerId?: string;
  customerEmail?: string;
  /** Precomputed HMAC over (customerId + ':' + email). */
  customerHash?: string;
  /** Shopify customer tags — used to read tier in future versions. */
  tags?: readonly string[];
}

const REQUEST_TYPE = 'sparrowForge.identityRequest';
const RESULT_TYPE = 'sparrowForge.identityResult';
const TIMEOUT_MS = 500;

let cached: SparrowIdentity | null = null;
let inflight: Promise<SparrowIdentity> | null = null;

/**
 * Ask the parent storefront for the current visitor's identity. Resolves to
 * an anonymous identity if no reply within 500ms (bridge not present /
 * older theme).
 *
 * Cached for the lifetime of the iframe — once we know the answer, we don't
 * keep round-tripping the parent.
 */
export function fetchIdentity(): Promise<SparrowIdentity> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  if (typeof window === 'undefined' || window === window.parent) {
    // Not in an iframe (dev / standalone preview) — no bridge to use.
    cached = { signedIn: false };
    return Promise.resolve(cached);
  }

  inflight = new Promise<SparrowIdentity>((resolve) => {
    const reqId = `id-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    let settled = false;
    const onMessage = (e: MessageEvent) => {
      const data = e?.data;
      if (!data || typeof data !== 'object') return;
      if (data.type !== RESULT_TYPE || data.reqId !== reqId) return;
      window.removeEventListener('message', onMessage);
      if (settled) return;
      settled = true;
      const result: SparrowIdentity = {
        signedIn: !!data.signedIn,
        // Liquid's `{{ customer.id | json }}` emits a NUMBER — coerce it.
        // The strict string check silently dropped customerId, so isReady()
        // failed and ALL cloud sync no-oped for signed-in users (2026-06-10).
        customerId:
          typeof data.customerId === 'string'
            ? data.customerId
            : typeof data.customerId === 'number' && Number.isFinite(data.customerId)
              ? String(data.customerId)
              : undefined,
        customerEmail: typeof data.customerEmail === 'string' ? data.customerEmail : undefined,
        customerHash: typeof data.customerHash === 'string' ? data.customerHash : undefined,
        tags: Array.isArray(data.tags) ? data.tags.filter((t: unknown): t is string => typeof t === 'string') : undefined,
      };
      cached = result;
      resolve(result);
    };
    window.addEventListener('message', onMessage);
    try {
      window.parent.postMessage({ type: REQUEST_TYPE, reqId }, '*');
    } catch {
      // Cross-origin postMessage shouldn't throw, but if the parent is
      // sandboxed in some unusual way we still want to resolve.
    }
    setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      cached = { signedIn: false };
      resolve(cached);
    }, TIMEOUT_MS);
  }).finally(() => {
    inflight = null;
  });

  return inflight;
}

/** Manually invalidate the cache (e.g., after a sign-in redirect). */
export function clearIdentityCache(): void {
  cached = null;
}

/**
 * Sign-in URL. We send the user to the Shopify customer account login,
 * with a `return_url` back to the Forge page so the redirect lands on
 * the Range Log with `?signedin=1`.
 */
export function getSignInUrl(returnPath = '/pages/the-forge?signedin=1'): string {
  // Shopify Customer Accounts new-format login URL.
  // The /account/login path is the universal entry — it routes to either
  // legacy or new customer accounts depending on the store's settings.
  return `/account/login?return_url=${encodeURIComponent(returnPath)}`;
}

/** Sign-out URL — Shopify's standard customer logout. */
export function getSignOutUrl(returnPath = '/pages/the-forge'): string {
  return `/account/logout?return_url=${encodeURIComponent(returnPath)}`;
}
